import {
  getCodeForSIM,
  getCurrentSIMCode,
  getESIMManufacturers,
  getSimTypes,
  updateESIMDetails,
  updateSimType,
} from '../../integration/e-sim.api';
import { getCookie } from '../../hooks/useCookie';
import { getCartDetails } from '../eir-cart/cart.util';
import { EIDAnalytics, SIMTypeAnalytics } from '../../../util/gtm';

/**
 * Validates the EID based on length and check digits algorithm (mod 97 == 1).
 * Sets validation state and error messages accordingly.
 *
 * @param {string} eidValue - The EID entered by the user.
 * @param {function} setIsValidEid - Function to update whether the EID is valid.
 * @param {function} setEidError - Function to set a descriptive validation error message.
 */
export const validateEid = (eidValue, setIsValidEid, setEidError) => {
  if (eidValue.length === 32) {
    // Ensure the EID is exactly 32 digits
    try {
      // Convert the EID value to BigInt
      const bigIntEid = BigInt(eidValue.trim()); // Remove any surrounding spaces
      // Perform the modulus check
      const remainder = bigIntEid % 97n; // Check the remainder when divided by 97
      // Check if the remainder equals 1
      if (remainder === 1n) {
        setIsValidEid(true); // Valid EID
        setEidError('');
      } else {
        setIsValidEid(false); // Invalid EID
        setEidError('Enter a valid EID number.');
      }
    } catch (error) {
      // If there’s an issue with the conversion (non-numeric input)
      console.error('EID Validation Error:', error);
      //  setEidError('Invalid EID format.');
      setIsValidEid(false);
    }
  } else {
    // If the EID is not exactly 32 digits, show error
    setIsValidEid(false);
    // setEidError('EID must be 32 digits.');
  }
};
/**
 * To update eSIM details using provided EID and manufacturer info.
 *
 * @param {Object} params - Parameters for updating eSIM details.
 * @param {string} params.eid - The EID number entered by the user.
 * @param {string} params.manufacturer - The selected manufacturer (e.g., "APPLE", "SAMSUNG").
 * @param {ApolloClient} params.graphql - Apollo GraphQL client instance for making the request.
 *
 * @returns {Promise<{ data?: any, error?: any }>} The API response, either data on success or error on failure.
 *
 */
export const handleESIMDetailsUpdate = async ({ eid, manufacturer, graphql, offerId, url }) => {
  try {
    const variables = {
      cartType: getCookie('cart_Type') ? getCookie('cart_Type') : 'NEW_ACQUISITION',
      reference: getCookie('reference'),
      offerId,
      eid,
      manufacturer,
    };

    const { data, error } = await updateESIMDetails({ graphql, variables });
    if (data) EIDAnalytics(manufacturer, url);
    return { data, error };
  } catch (err) {
    console.error('Unexpected error in handleESIMDetailsUpdate:', err);
    return { error: err };
  }
};
/**
 * Handles the EID input change.
 * Updates the EID value and optionally triggers validation based on selected brand.
 *
 * @param {object} e - The change event from the EID input.
 * @param {string} brand - The selected brand to conditionally trigger validation.
 * @param {function} setEid - Function to update the EID state.
 * @param {function} validateEidFn - Callback function to validate the EID.
 */
export const handleEidChange = async (e, brand, setEid, isValidEid, setIsValidEid, setEidError) => {
  const { value } = e.target;
  setEid(value);
  // ValiEdate only if Apple or Samsung is selected

  validateEid(value, setIsValidEid, setEidError);
};
/**
 * Handles the brand selection change.
 * Resets EID-related state when a new brand is selected.
 *
 * @param {object} e - The change event from the brand dropdown.
 * @param {function} setBrand - Function to update the selected brand state.
 * @param {function} setEid - Function to clear the EID input.
 * @param {function} setIsValidEid - Function to reset EID validation status.
 * @param {function} setEidError - Function to clear EID error messages.
 */
export const handleBrandChange = (e, setBrand, setEid, setIsValidEid, setEidError) => {
  const selectedBrand = e.target.value;
  setBrand(selectedBrand);
  setEid('');
  setIsValidEid(true);
  setEidError('');
};

/**
 * Fetches the available SIM types from the API.
 *
 * Makes a GraphQL query via Apollo client and returns the result.
 *
 * @param {object} graphql - The Apollo GraphQL client instance.
 * @returns {Promise<Array>} - A list of SIM types returned from the API (e.g., ["ESIM", "All_IN_ONE"]).
 * Returns an empty array if an error occurs.
 */
export const fetchSimTypes = async graphql => {
  const { data, error } = await getSimTypes({ graphql });
  if (error) {
    console.error('Error fetching SIM types:', error);
  }
  return data?.getSIMTypes || [];
};

export const mapApiToInternalSimType = {
  ALL_IN_1: 'physical',
  ESIM: 'esim',
};

export const mapInternalToApiSimType = {
  physical: 'ALL_IN_1',
  esim: 'ESIM',
};
/**
 * To update sim details using provided simType.
 *
 * @param {Object} params - Parameters for updating SIM details.
 * @param {string} params.simType - Selected sim type[ALL_IN_1 or ESIM].
 * @param {ApolloClient} params.graphql - Apollo GraphQL client instance for making the request.
 *
 * @returns {Promise<{ data?: any, error?: any }>} The API response, either data on success or error on failure.
 *
 */
export const handleSimTypeUpdate = async ({
  SIMType,
  offerId,
  graphql,
  localStore,
  offerCode,
  url,
}) => {
  try {
    const variables = {
      cartType: getCookie('cart_Type') ? getCookie('cart_Type') : 'NEW_ACQUISITION',
      reference: getCookie('reference') || localStore.getLocalStorage('PROSPECTUUID'),
      offerId,
      simCardType: SIMType, // Directly use simType from state
      offerCode,
    };
    const { data } = await updateSimType({ graphql, variables });
    if (data) SIMTypeAnalytics(SIMType === 'ESIM' ? 'ESIMBILL1' : '1SIMBILLE', url);
    return data ? getCartDetails(data?.updateSIMType) : null;
  } catch (err) {
    console.error('Unexpected error in handleSimTypeUpdate:', err);
    return null;
  }
};

/**
 * Fetches the available ESIM Manufacturers from the API.
 *
 * Makes a GraphQL query via Apollo client and returns the result.
 *
 * @param {object} graphql - The Apollo GraphQL client instance.
 * @returns {Promise<Array>} - A list of ESIM Manufacturers returned from the API (e.g., ["Apple", "Samsung"]).
 * Returns an empty array if an error occurs.
 */
export const fetchESIMManufacturers = async graphql => {
  const cartType = getCookie('cart_Type');
  const variables = {
    cartType,
  };
  const { data, error } = await getESIMManufacturers({ graphql, variables });
  if (error) {
    console.error('Error fetching SIM types:', error);
  }
  return data?.getESIMManufacturers || [];
};

/**
 * Fetches the current SIM Code of the mobile offer from the API.
 *
 * Makes a GraphQL query via Apollo client and returns the result.
 *
 * @param {object} graphql - The Apollo GraphQL client instance.
 * @returns {Promise<String>} - Returns a string.
 * Returns current SIM Code .
 */
export const getCOCurrentSIM = async graphql => {
  const variables = {
    subscriptionId: parseInt(getCookie('subscription_id'), 10),
  };
  const { data, error } = await getCurrentSIMCode({ graphql, variables });
  if (error) {
    console.error('Error fetching SIM types:', error);
  }
  return data?.getCurrentSIMCode;
};

/**
 * Fetches the SIM Code  from the API.
 *
 * Makes a GraphQL query via Apollo client and returns the result.
 *
 * @param {object} graphql - The Apollo GraphQL client instance.
 * @param {String} simCardType - The Sim cart type either ALL_IN_1 or ESIM.
 * @param {String} catalogOfferCode - Mobile offer code.
 * @returns {Promise<String>} - Returns a string.
 *
 * Returns SIM Code for the SIM type.
 */
export const getCodeForSimType = async (graphql, simCardType, catalogOfferCode) => {
  const variables = {
    simCardType,
    catalogOfferCode,
  };
  if (!getCookie('PREPAY_REFERENCE')) {
    const { data, error } = await getCodeForSIM({ graphql, variables });

    if (error) {
      console.error('Error fetching SIM types:', error);
    }
    return data?.getCodeForSimType;
  }
};
